<template>
  <div
    class="navbar-page-title d-flex align-items-center flex-grow-1 flex-wrap min-w-0"
    :class="{
      'navbar-page-title--with-beside': !!displayBeside,
      'navbar-page-title--with-subtitle': !!displaySubtitle,
    }"
  >
    <div class="page-title-stack min-w-0">
      <div class="page-title-row d-flex align-items-center flex-wrap min-w-0">
    <span
      v-if="displayTitle"
      class="page-title-text text-truncate"
      :class="{ 'page-title-text--eyebrow': isEyebrowTitle }"
    >{{ displayTitle }}</span>

    <template v-if="displayBeside">
      <span
        class="page-title-beside-sep flex-shrink-0"
        aria-hidden="true"
      />
      <span class="page-title-beside text-truncate">{{ displayBeside }}</span>
    </template>

    <div
      v-if="hasBreadcrumbTrail"
      class="page-title-breadcrumb d-flex align-items-center text-truncate"
      :class="{ 'page-title-breadcrumb--solo': !displayTitle && !displayBeside }"
    >
      <router-link
        to="/"
        class="breadcrumb-trail-home d-flex align-items-center flex-shrink-0"
      >
        <feather-icon
          icon="HomeIcon"
          size="12"
        />
      </router-link>

      <template v-for="(item, index) in trailItems">
        <feather-icon
          :key="`sep-${index}`"
          icon="ChevronRightIcon"
          size="12"
          class="breadcrumb-trail-separator flex-shrink-0"
        />
        <router-link
          v-if="item.to && !item.active"
          :key="`link-${index}`"
          :to="item.to"
          class="breadcrumb-trail-item d-inline-block text-truncate"
        >
          {{ item.text }}
        </router-link>
        <span
          v-else
          :key="`text-${index}`"
          class="breadcrumb-trail-item breadcrumb-trail-item--active d-inline-block text-truncate"
        >
          {{ item.text }}
        </span>
      </template>
    </div>
      </div>
      <span
        v-if="displaySubtitle"
        class="page-title-subtitle text-truncate"
      >{{ displaySubtitle }}</span>
    </div>
  </div>
</template>

<script>
import { $themeConfig } from '@themeConfig'

/**
 * Replaces the old Bookmarks icon row (Calendar / Todo / Policy Documents —
 * see `search-and-bookmark-data.js`'s `isBookmarked: true` entries) in this
 * same navbar slot with the CURRENT PAGE's title, per product direction:
 * those 3 quick-link icons added little value versus always knowing which
 * page you're on, especially once the Task Management page's own header
 * got compacted (see `TaskBoard.vue`) and no longer repeats its title as
 * prominently as before.
 *
 * Title resolution, most-specific first — every route in this app SHOULD
 * carry a `meta.pageTitle` (same field `AppBreadcrumb.vue` used to read
 * for the page's `<h2>`, before that row-2 header was folded in here), but
 * this degrades gracefully instead of ever rendering blank:
 *   1. `route.meta.pageTitle` (e.g. "Task Management").
 *   2. The last/active breadcrumb item's `text` (`route.meta.breadcrumb`),
 *      for a route that set a breadcrumb trail but not a bare pageTitle.
 *   3. The route's own `name`, humanized (`task-board` -> "Task Board").
 *   4. Empty string — NEVER fall back to `$themeConfig.app.appName`
 *      ("Sattva"). That value already renders as the sidebar `.brand-text`
 *      wordmark next to the logo icon; repeating it here read as a
 *      duplicated teal brand title in the sticky navbar.
 *
 * The breadcrumb trail (home icon + `route.meta.breadcrumb` path) that used
 * to render as its own row-2 `AppBreadcrumb.vue` block in the content area
 * now renders inline here, BESIDE the title (a thin vertical divider
 * separates the two — see `.page-title-breadcrumb`'s `border-left` in the
 * `<style>` below), reading from the exact same `route.meta.breadcrumb`
 * array — no forked/duplicated trail-building logic. (Previously stacked
 * on its own line below the title — a same-line layout reads better and
 * doesn't need the extra vertical space.) The one difference: any trail
 * item whose text duplicates the title we're already showing right next
 * to it (e.g. Task Management's own breadcrumb is just
 * `[{ text: 'Task Management', active: true }]`) is filtered out, since
 * repeating identical text twice in the same compact navbar row reads as
 * a bug rather than a real trail. Items that add real context (e.g.
 * "Jobs" on the "Add Job" page) still show through.
 */
function humanizeRouteName(name) {
  if (!name) return null
  return name
    .split(/[-_]/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export default {
  name: 'PageTitle',
  computed: {
    displayTitle() {
      const meta = this.$route?.meta || {}
      if (meta.pageTitle) {
        // Guard against a misconfigured route that sets pageTitle to the
        // brand name — same duplication bug as the old appName fallback.
        const appName = ($themeConfig.app.appName || '').trim().toLowerCase()
        if (appName && meta.pageTitle.trim().toLowerCase() === appName) return ''
        return meta.pageTitle
      }
      const breadcrumb = Array.isArray(meta.breadcrumb) ? meta.breadcrumb : []
      const activeCrumb = breadcrumb.find(item => item.active) || breadcrumb[breadcrumb.length - 1]
      if (activeCrumb && activeCrumb.text) return activeCrumb.text
      return humanizeRouteName(this.$route?.name) || ''
    },
    // Opt-in via `meta.pageTitleEyebrow` — used by Client Dashboard so the
    // teal uppercase label lives in the navbar title instead of duplicating
    // above the in-page client name heading.
    isEyebrowTitle() {
      return Boolean(this.$route?.meta?.pageTitleEyebrow)
    },
    // Compact description under the navbar title (POFU / Interview / Bulk Mailer).
    // Set via `meta.pageSubtitle` so pages do not repeat an in-card H1.
    displaySubtitle() {
      const subtitle = this.$route?.meta?.pageSubtitle
      return subtitle ? String(subtitle).trim() : ''
    },
    // Client name (or other route param) shown beside an eyebrow title —
    // e.g. "CLIENT DASHBOARD · Radius synergies" on one navbar line.
    displayBeside() {
      if (!this.isEyebrowTitle) return ''
      const raw = this.$route?.params?.clientName
      if (raw == null || raw === '') return ''
      let str = String(raw).replace(/\+/g, ' ')
      for (let i = 0; i < 3; i += 1) {
        try {
          const decoded = decodeURIComponent(str)
          if (decoded === str) break
          str = decoded
        } catch (e) {
          break
        }
      }
      return str.trim()
    },
    trailItems() {
      const breadcrumb = this.$route?.meta?.breadcrumb
      if (!Array.isArray(breadcrumb)) return []
      const title = (this.displayTitle || '').trim().toLowerCase()
      return breadcrumb.filter(item => (item.text || '').trim().toLowerCase() !== title)
    },
    hasBreadcrumbTrail() {
      return this.trailItems.length > 0
    },
  },
}
</script>

<style scoped>
/*
 * Compact title + breadcrumb in the floating navbar (2026-07-24).
 * Shared globally on purpose — keep density tasteful for every page,
 * not only Create Invoice. Divider uses a ::before so trail text can
 * stay independently quieter without parent `opacity` washing it out.
 */
.navbar-page-title {
  min-width: 0;
  gap: 0;
  /* Optical center in the compact floating navbar bar */
  align-self: center;
}

.page-title-stack {
  min-width: 0;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.page-title-row {
  min-width: 0;
  max-width: 100%;
}

.navbar-page-title--with-subtitle {
  align-items: center;
}

.page-title-subtitle {
  display: block;
  font-size: 0.6875rem;
  font-weight: 400;
  line-height: 1.3;
  letter-spacing: 0;
  color: inherit;
  opacity: 0.58;
  margin-top: 0.12rem;
  max-width: min(34rem, 42vw);
}

/* Client Dashboard: eyebrow label + client name on one line */
.navbar-page-title--with-beside {
  row-gap: 0.15rem;
}

.page-title-text {
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: inherit;
  line-height: 1.2;
}

/* Matches former UnifiedClientDashboard `.dashboard-page-header__eyebrow` */
.page-title-text--eyebrow {
  flex-shrink: 0;
  max-width: none;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #00ac99;
  line-height: 1.15;
}

/*
 * Thin muted pipe between eyebrow and client name — same visual language
 * as `.page-title-breadcrumb::before`, avoids tinted middle-dot glyphs.
 */
.page-title-beside-sep {
  align-self: center;
  width: 1px;
  height: 0.9em;
  margin: 0 0.65rem;
  padding: 0;
  background: currentColor;
  opacity: 0.22;
}

.page-title-beside {
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: -0.015em;
  color: inherit;
  line-height: 1.25;
  min-width: 0;
  max-width: min(20rem, 42vw);
}

@media (max-width: 991.98px) {
  .page-title-subtitle {
    max-width: min(28rem, 70vw);
  }
}

@media (max-width: 575.98px) {
  .page-title-beside-sep {
    display: none;
  }

  .page-title-beside {
    flex-basis: 100%;
    max-width: 100%;
    margin-top: 0.05rem;
    font-size: 0.9375rem;
  }
}

.page-title-breadcrumb {
  position: relative;
  min-width: 0;
  margin-left: 0.5rem;
  padding-left: 0.5rem;
  font-size: 0.7rem;
  font-weight: 400;
  line-height: 1.2;
}

.page-title-breadcrumb--solo {
  margin-left: 0;
  padding-left: 0;
}

.page-title-breadcrumb::before {
  content: '';
  position: absolute;
  left: 0;
  top: 18%;
  bottom: 18%;
  width: 1px;
  background: currentColor;
  opacity: 0.22;
}

.page-title-breadcrumb--solo::before {
  display: none;
}

.breadcrumb-trail-home,
.breadcrumb-trail-item {
  color: inherit;
  opacity: 0.62;
  min-width: 0;
  max-width: 14rem;
}

.breadcrumb-trail-home:hover,
.breadcrumb-trail-item:not(.breadcrumb-trail-item--active):hover {
  opacity: 0.9;
  text-decoration: underline;
}

.breadcrumb-trail-item--active {
  cursor: default;
  opacity: 0.72;
}

.breadcrumb-trail-separator {
  margin: 0 0.2rem;
  opacity: 0.4;
}
</style>
